import { useMutation } from '@tanstack/react-query';
import { Flex, Image } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';

import { ApproveApi } from 'apis/ApproveApis';
import ModalMapLocation from 'components/Map/ModalMapLocation';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import {
  ICustomerApproval,
  IUserApproval,
  TGetUserApprovalDetail,
} from 'interfaces/Approve/Approve';
import path from 'routes/Path';
import { toastError } from 'utils/constants/functions';
import ApprovalBoard, {
  IApprovalContentItem,
} from './Components/ApprovalBoard';
import ApproveDetailWrapper, {
  IUserApprovalInfo,
} from './Components/ApproveDetailWrapper';

const initData = {} as IUserApproval;
const initUserApproval = {} as IUserApprovalInfo;
const initCustomerData = {} as ICustomerApproval;
const initApprovalData = [] as IApprovalContentItem[];

const CustomerApprove = () => {
  const { user } = useUser();
  const queryParam = useQueryParam();
  const { setTitle, setBreadcrumb } = useTitle();

  const approvalId = queryParam.get('id');

  const [customerData, setCustomerData] =
    useState<ICustomerApproval>(initCustomerData);
  const [userApproval, setUserApproval] =
    useState<IUserApprovalInfo>(initUserApproval);
  const [approvalData, setApprovalData] =
    useState<IApprovalContentItem[]>(initApprovalData);

  const [data, setData] = useState<IUserApproval>(initData);
  const [isOpenMapModal, setIsOpenMapModal] = useState(false);

  const {
    mutate: getUserApprovalById,
    isPending: isGetUserApprovalByIdPending,
  } = useMutation({
    mutationFn: (params: TGetUserApprovalDetail) =>
      ApproveApi.getUserApprovalById(params),
    onSuccess: (res) => {
      const item = res.data?.getUserApproval?.items?.[0];
      if (!Object.keys(item).length) {
        toastError('Không tìm thấy thông tin phê duyệt');
        return;
      }

      setData(item);
      getUserCustomerApproval(item?.id);
      refetchCustomerApproval(item?.idConnect);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const {
    mutate: getUserCustomerApproval,
    isPending: isGetUserCustomerApprovalPending,
  } = useMutation({
    mutationFn: (id: string) => ApproveApi.getUserCustomerApproval(id),
    onSuccess: (res) => {
      if (res.data?.getUserApprovalDetail) {
        const item = (res.data?.getUserApprovalDetail?.items ?? [])[0];

        setUserApproval({
          plantId: item?.plantId,
          dateSent: item?.createDate,
          // status: item?.tuserApproval?.tstatusApproval?.name,
          status: item?.tuserApprovalDetail?.find(
            (item: any) => item?.email === user?.email,
          )?.approvalYn,
          fullName: item?.tcreateUser?.fullName,
          jobTitle: item?.tcreateUser?.jobTitle,
          note: '',
        });
      }
    },
    onError: (error) => {
      toastError(
        `Lỗi khi lấy thông tin người gửi phê duyệt. ${error?.message ?? error}`,
      );
    },
  });

  const {
    mutate: getCustomerApprovalMutate,
    isPending: isGetCustomerApprovalPending,
  } = useMutation({
    mutationFn: (customerId: string) =>
      ApproveApi.getCustomerApproval(customerId),
    onSuccess: (res) => {
      const item: ICustomerApproval = res.data?.getCustomer?.items?.[0];

      if (!item) {
        toastError('Không tìm thấy thông tin khách hàng');
        return;
      }

      setCustomerData(item);
    },
  });

  // const { mutate: updateApprovalStatus } = useMutation({
  //   mutationFn: (params: IUpdateApprovalStatus) =>
  //     ApproveApi.updateApprovalStatus(params),
  //   onSuccess: () => {
  //     toastSuccess('Thành công');

  //     if (!state?.id) return;
  //     getUserApprovalById(state.id);
  //   },
  //   onError: (error) => {
  //     console.log(error);
  //     toastError(`Phê duyệt thất bại: ${error.message}`);
  //   },
  // });

  const refetchCustomerApproval = useCallback((id: string) => {
    getCustomerApprovalMutate(id);
  }, []);

  const refetch = useCallback(() => {
    if (!approvalId) return;

    const params: TGetUserApprovalDetail = {
      // id: state?.id,
      id: approvalId,
      ...(user?.isAdmin || user?.isHO
        ? {}
        : {
            tuserApprovalDetail: user?.email,
          }),
    };

    getUserApprovalById(params);
  }, [approvalId, user]);

  useEffect(() => {
    // if (!Object.keys(state).length) {
    //   navigate(path.APPROVE);
    //   return;
    // }

    setTitle('Phê duyệt khách hàng mới');
    setBreadcrumb([
      { title: 'Phê duyệt', href: path.APPROVE },
      { title: 'Phê duyệt khách hàng mới' },
    ]);

    // if (!state?.id) return;
    // getUserApprovalById(state.id);
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!Object.keys(customerData).length) return;

    const imageUrls = Object.entries(customerData)
      .filter(([key]) => key.startsWith('urlImage'))
      .map(([_, value]) => value)
      .filter((value) => !!value);

    const { tcity, tdistrict, tward, address } = customerData;
    const formattedAddress = [address, tward.name, tdistrict.name, tcity.name]
      .filter(Boolean)
      .join(', ');

    setApprovalData([
      {
        title: 'Thông tin',
        children: [
          {
            content: [
              {
                title: 'Loại khách hàng',
                content:
                  customerData.typeSap === '1' ? 'Cá nhân' : 'Doanh nghiệp',
              },
              {
                title: 'Mã cha',
                content: customerData.mainCustomer?.sapCustomerId,
              },
              {
                title: 'Tên ngắn',
                content: customerData.shortName,
              },
              {
                title: 'MST',
                content: customerData.taxId,
              },
            ],
          },
          {
            content: [
              {
                title: 'Danh xưng',
                content:
                  customerData.titleSap === '0001'
                    ? 'Nữ'
                    : customerData.titleSap === '0002'
                      ? 'Nam'
                      : 'Công ty',
              },
            ],
          },
        ],
      },
      {
        title: 'Liên hệ',
        children: [
          {
            content: [
              {
                title: 'Người liên hệ',
                content: customerData.name,
              },
              {
                title: 'CCCD',
                content: customerData.cccd,
              },
              {
                title: 'Giới tính',
                content: customerData.gender,
              },
            ],
          },
          {
            content: [
              {
                title: 'SDT',
                content: customerData.phone,
              },
              {
                title: 'Ngày sinh',
                content:
                  customerData.dayofbirth &&
                  dayjs(customerData.dayofbirth).format('DD/MM/YYYY'),
              },
              {
                title: 'Email',
                content: customerData.email,
              },
            ],
          },
        ],
      },
      {
        title: 'Hình ảnh',
        children: [
          {
            content: [
              {
                title: '',
                content: (
                  <Flex gap={16}>
                    {imageUrls?.map((imageUrl, key) => (
                      <Image
                        key={key}
                        width={118}
                        height={118}
                        src={imageUrl}
                        className="object-contain"
                      />
                    ))}
                  </Flex>
                ),
              },
            ],
          },
        ],
      },
      {
        title: 'Ghi chú',
        children: [
          {
            content: [
              {
                title: 'Dòng hàng kinh doanh',
                content: customerData.tcustomerLineProduct
                  ?.map((item) =>
                    item?.lineProductId === 'K'
                      ? item?.note
                      : item?.lineProducts?.name,
                  )
                  .join(', '),
              },
              {
                title: 'Ghi chú',
                content: customerData.note,
              },
            ],
          },
        ],
      },
      {
        title: 'Địa chỉ',
        children: [
          {
            content: [
              {
                title: 'Địa chỉ',
                content: (
                  <span>
                    <span>{formattedAddress}</span>
                    <span
                      onClick={() => setIsOpenMapModal(true)}
                      className="font-bold text-[#003056] ml-2 cursor-pointer hover:text-[#3d7199]"
                    >
                      Xem bản đồ
                    </span>
                  </span>
                ),
              },
              {
                title: 'Toạ độ X',
                content: (
                  <span>
                    <strong>{customerData.latitude}</strong>
                    <span className="mx-1 font-normal">-</span>
                    <span className="font-normal mr-[5px]">Toạ độ Y:</span>
                    <strong>{customerData.longitude}</strong>
                  </span>
                ),
              },
            ],
          },
        ],
      },
    ]);
  }, [customerData]);

  // const handleApprove = () => {
  //   if (!data?.tuserApprovalDetail?.length) {
  //     toastError('Không tim thấy thông tin phê duyệt!');
  //     return;
  //   }

  //   updateApprovalStatus({
  //     id: data?.tuserApprovalDetail?.[0]?.id,
  //     approvalYn: true,
  //   });
  // };

  // const handleCorrectionRequest = () => {
  //   if (!data?.tuserApprovalDetail?.length) {
  //     toastError('Không tim thấy thông tin phê duyệt!');
  //     return;
  //   }

  //   updateApprovalStatus({
  //     id: data?.tuserApprovalDetail?.[0]?.id,
  //     approvalYn: false,
  //   });
  // };

  return (
    <>
      <ApproveDetailWrapper
        data={data}
        userApproval={userApproval}
        title={`${customerData?.storeName || 'Khách hàng mới'}`}
        loading={
          isGetUserApprovalByIdPending ||
          isGetCustomerApprovalPending ||
          isGetUserCustomerApprovalPending
        }
        refetch={refetch}
        // approvalId={data?.id}
        // approvalStatus={data?.statusApproval}
        // refetch={() => getUserApprovalById(state.id)}
        // onApprove={handleApprove}
        // onCorrectionRequest={handleCorrectionRequest}
      >
        <ApprovalBoard data={approvalData} />
      </ApproveDetailWrapper>
      <ModalMapLocation
        title="Bản đồ"
        open={isOpenMapModal}
        center={{
          lat: parseFloat(customerData?.latitude) || null,
          lng: parseFloat(customerData?.longitude) || null,
        }}
        setOpen={setIsOpenMapModal}
      />
    </>
  );
};

export default CustomerApprove;
